// VOY-461 — useVoyMyTrip: drives a Voy My Trip session over the SSE stream.
//
// Owns the VoyMyTripSession and threads build/refine through the streaming
// client, updating component cards incrementally as events arrive. The
// refine paths (NL utterance + pill set) both flow through one update path —
// the server returns the authoritative constraintSet so pills and the NL
// summary never drift.

import { useCallback, useRef, useState } from 'react'
import { buildTrip, refineTrip } from '@/lib/voy-my-trip'
import type {
  VoyMyTripSession,
  BuildTripRequest,
  ComponentResult,
} from '@/types/voy-my-trip'
import type {
  RefinementConstraintSet,
  ConstraintType,
  TripComponent,
} from '@/lib/search/refinement-constraints'

const EMPTY_SESSION: VoyMyTripSession = {
  sessionId: null,
  framing: null,
  constraintSet: null,
  components: {},
  provenance: null,
  trip_plan_id: null,
  streaming: false,
  reactions: null,
  changeLog: [],
}

/** Which constraint types differ between two sets (for "changed" pill highlight). */
function changedConstraintTypes(
  prev: RefinementConstraintSet | null,
  next: RefinementConstraintSet | null,
): ConstraintType[] {
  if (!prev || !next) return []
  const key = (c: { type: string; value: unknown }) =>
    `${c.type}:${JSON.stringify(c.value)}`
  const prevKeys = new Set(prev.constraints.map(key))
  const nextKeys = new Set(next.constraints.map(key))
  const types = new Set<ConstraintType>()
  for (const c of next.constraints) if (!prevKeys.has(key(c))) types.add(c.type)
  for (const c of prev.constraints) if (!nextKeys.has(key(c))) types.add(c.type)
  return [...types]
}

export function useVoyMyTrip() {
  const [session, setSession] = useState<VoyMyTripSession>(EMPTY_SESSION)
  const [changedTypes, setChangedTypes] = useState<ConstraintType[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const changeSeq = useRef(0)

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  /**
   * Append a refine's confirmation to the changes log (only when it applied an
   * op) and clear the redundant inline notice — a failed parse keeps its notice
   * (no op) so the user is told to rephrase; an applied change shows in the log.
   */
  const logChange = useCallback((d: { ops?: unknown[]; message?: string; recovered?: boolean }) => {
    if (!Array.isArray(d?.ops) || d.ops.length === 0) return
    changeSeq.current += 1
    const entry = { id: changeSeq.current, text: d.message || 'Updated your trip.', recovered: !!d.recovered }
    setSession((s) => ({ ...s, changeLog: [...(s.changeLog ?? []), entry], lastMessage: undefined }))
  }, [])

  const handlers = useCallback(
    () => ({
      onComponent: (c: ComponentResult) =>
        setSession((s) => ({ ...s, components: { ...s.components, [c.type]: c } })),
      onDone: (d: Parameters<NonNullable<Parameters<typeof buildTrip>[1]['onDone']>>[0]) =>
        setSession((s) => ({
          ...s,
          streaming: false,
          sessionId: d.sessionId,
          framing: d.framing,
          constraintSet: d.constraintSet,
          provenance: d.provenance,
          trip_plan_id: d.trip_plan_id,
          lastMessage: d.message,
        })),
      onError: (e: { error: string }) =>
        setSession((s) => ({ ...s, streaming: false, lastMessage: e.error })),
    }),
    [],
  )

  /** Assemble — zero/minimal input → personalized trip. */
  const build = useCallback(
    (req: BuildTripRequest = {}) => {
      cancel()
      const ac = new AbortController()
      abortRef.current = ac
      setChangedTypes([])
      setSession({ ...EMPTY_SESSION, streaming: true })
      return buildTrip(req, handlers(), ac.signal)
    },
    [cancel, handlers],
  )

  /** Mark reload/held components as loading immediately for snappy feedback. */
  const markReactionsLoading = useCallback(
    (reactions: Record<TripComponent, import('@/lib/search/refinement-constraints').ComponentReaction>) => {
      setSession((s) => {
        const components = { ...s.components }
        for (const c of Object.keys(reactions) as TripComponent[]) {
          if (reactions[c] === 'reload' && components[c]) components[c] = { ...components[c]!, state: 'loading' }
        }
        return { ...s, components, reactions }
      })
    },
    [],
  )

  /** Refine via natural language. */
  const refineUtterance = useCallback(
    (utterance: string) => {
      if (!session.sessionId) return Promise.resolve()
      const prevSet = session.constraintSet
      cancel()
      const ac = new AbortController()
      abortRef.current = ac
      setSession((s) => ({ ...s, streaming: true }))
      return refineTrip(
        { sessionId: session.sessionId, utterance, constraintSet: prevSet },
        {
          onStart: (d) => { if (d.reactions) markReactionsLoading(d.reactions) },
          onComponent: handlers().onComponent,
          onDone: (d) => { handlers().onDone(d); setChangedTypes(changedConstraintTypes(prevSet, d.constraintSet)); logChange(d) },
          // clear changed-pill highlights on failure so a failed NL refine doesn't
          // leave the prior refinement's pills stuck as "changed" (mirrors refinePills)
          onError: (e) => { handlers().onError(e); setChangedTypes([]) },
        },
        ac.signal,
      )
    },
    [session.sessionId, session.constraintSet, cancel, handlers, markReactionsLoading, logChange],
  )

  /**
   * Swap an alternative into the primary slot for a component (client-side,
   * no re-search). The displaced primary drops to the front of alternatives
   * so the user can swap back. Makes the "N alternatives" affordance real.
   */
  const swapAlternative = useCallback((type: TripComponent, altIndex: number) => {
    setSession((s) => {
      const comp = s.components[type]
      const alt = comp?.alternatives?.[altIndex]
      if (!comp || !alt) return s
      const nextAlts = comp.alternatives!.slice()
      nextAlts.splice(altIndex, 1)
      // Only ever called for flights/hotels (itinerary has no swap UI), so the
      // displaced primary is a FlightResult|Hotel — safe to re-add as an alt.
      if (comp.primary) nextAlts.unshift(comp.primary as ComponentResult['alternatives'][number])
      return { ...s, components: { ...s.components, [type]: { ...comp, primary: alt, alternatives: nextAlts } } }
    })
  }, [])

  /** Refine via a pill-computed complete next set. */
  const refinePills = useCallback(
    (nextSet: RefinementConstraintSet) => {
      if (!session.sessionId) return Promise.resolve()
      const prevSet = session.constraintSet
      setChangedTypes(changedConstraintTypes(prevSet, nextSet))
      cancel()
      const ac = new AbortController()
      abortRef.current = ac
      setSession((s) => ({ ...s, streaming: true }))
      return refineTrip(
        { sessionId: session.sessionId, nextSet, constraintSet: prevSet },
        {
          onStart: (d) => { if (d.reactions) markReactionsLoading(d.reactions) },
          onComponent: handlers().onComponent,
          onDone: (d) => { handlers().onDone(d); logChange(d) },
          // changedTypes was set optimistically before the stream; clear it on
          // failure so the pill strip doesn't keep stale "changed" highlights.
          onError: (e) => { handlers().onError(e); setChangedTypes([]) },
        },
        ac.signal,
      )
    },
    [session.sessionId, session.constraintSet, cancel, handlers, markReactionsLoading, logChange],
  )

  return { session, changedTypes, build, refineUtterance, refinePills, swapAlternative, cancel }
}
